import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck } from "@phosphor-icons/react";

export default function ChangePassword() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const forced = user?.must_change_password;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.new_password !== form.confirm) {
      setError("New passwords do not match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      setUser({ ...user, must_change_password: false });
      toast.success("Password updated");
      navigate(user.role === "admin" ? "/admin" : "/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <form onSubmit={submit} className="w-full max-w-sm" data-testid="change-password-form">
        <div className="w-12 h-12 bg-primary flex items-center justify-center mb-6">
          <ShieldCheck size={24} weight="bold" className="text-primary-foreground" />
        </div>
        <div className="label-caps mb-2">{forced ? "Temporary password" : "Security"}</div>
        <h2 className="font-display font-bold text-3xl tracking-tighter mb-2">Set a new password</h2>
        <p className="text-sm text-muted-foreground mb-8">
          {forced
            ? "For your security, please replace the temporary password you were emailed."
            : "Update your account password below."}
        </p>

        <div className="mb-4">
          <Label className="label-caps">{forced ? "Temporary password" : "Current password"}</Label>
          <Input type="password" className="rounded-none mt-2 h-11" value={form.current_password} onChange={upd("current_password")} required data-testid="current-password-input" />
        </div>
        <div className="mb-4">
          <Label className="label-caps">New password</Label>
          <Input type="password" className="rounded-none mt-2 h-11" value={form.new_password} onChange={upd("new_password")} required data-testid="new-password-input" />
        </div>
        <div className="mb-4">
          <Label className="label-caps">Confirm new password</Label>
          <Input type="password" className="rounded-none mt-2 h-11" value={form.confirm} onChange={upd("confirm")} required data-testid="confirm-password-input" />
        </div>

        {error && (
          <div className="mb-4 text-destructive text-sm font-mono border border-destructive/40 bg-destructive/5 px-3 py-2" data-testid="change-password-error">
            {error}
          </div>
        )}

        <Button type="submit" disabled={busy} className="w-full rounded-none h-11" data-testid="change-password-submit">
          {busy ? "Saving…" : "Update password"}
        </Button>
        <button type="button" onClick={logout} className="mt-6 text-sm text-muted-foreground hover:text-foreground transition-colors w-full text-center" data-testid="change-password-signout">
          Sign out
        </button>
      </form>
    </div>
  );
}
